/**
 * "Start" — the approved InMails go out through the FINN Outreach Sender extension (Sofia's
 * decision, Oct 15 2026): one click here, and the extension opens each candidate's Recruiter
 * composer in this Chrome, types subject and message, presses Send and answers. This page then
 * records the InMail as sent, exactly as "Mark as sent" on the card does. One candidate at a time,
 * in order; the first refusal stops the run so nothing is sent blind. At most 25 per Start, with a
 * 45–120 s pause between two InMails that went out, and a Stop (Sofia, Oct 29 2026, `sendPacing.ts`).
 *
 * "Test fill" is the same run without the Send press — the way to see, on Theresa's test
 * pipeline, that LinkedIn's composer is found and filled before anything real goes out.
 *
 * Closing the laptop mid-run (Sofia, Nov 2 2026, `wakeDetector.ts`): asleep during a pause, the pause
 * simply ends on wake (its end is a clock time, not a timer) and the run goes on once online. Asleep
 * or offline while an InMail was open, or before it was recorded as sent, the run STOPS and that row
 * says to check LinkedIn — whether it went out cannot be known here, and a second send must never
 * happen. The rest waits for the next Start.
 */
import { useEffect, useRef, useState } from 'react';
import { showToast } from '@finn-auto/design-system/atoms/toast';
import { logAnalyticsEvent } from '../../../../analytics/nativeAnalytics';
import * as api from '../../services/taOutreachApi';
import { editsReplacedBy, signForMe } from './redraftWhy';
import type { OutreachCandidate } from '../../types/taOutreach';
import { Badge, Card } from './outreachUi';
import { composerUrl, sendsInProject } from './outreachOpen';
import { RecruiterLinkFix } from './RecruiterLinkFix';
import { ExtensionSetup } from './ExtensionSetup';
import { weekdayFollowUpDays } from './followUpDays';
import { canSendInProject, explain, extensionVersion, runInExtension, type SendResult } from './outreachExtension';
import { canSendInWindow, closeSendWindow, openSendWindow, screenArea } from './outreachExtension';
import { SEND_BATCH, clock, pauseMs } from './sendPacing';
import { useWakeDetector, type WakeDetector } from './wakeDetector';

export const SLEPT_TEXT = 'Laptop slept while this InMail was open — check in LinkedIn whether it went out, then Mark as sent or Start again.';

const btnP = 'h-8 rounded-md bg-[#0072EA] px-3.5 text-xs font-semibold text-white hover:bg-[#0167D4] disabled:opacity-40';
const btnO = 'h-8 rounded-md border border-solid border-[#1A1A1A] bg-white px-3.5 text-xs font-semibold hover:bg-[#1A1A1A] hover:text-white disabled:opacity-40';

/** LinkedIn's follow-up block asks for its own subject; it reads as a reply to the first one. */
const followUpSubject = (subject: string) => (subject ? `Re: ${subject}`.slice(0, 80) : '');

type RowState =
  | { kind: 'waiting' }
  | { kind: 'working' }
  | { kind: 'filled'; sendButton: string; detail: string; followUp: boolean; followUpDays?: number | null; links?: SendResult['links']; paragraphsLost?: string | null; contactedWarning?: string | null }
  | { kind: 'sent'; verified: boolean }
  | { kind: 'failed'; text: string; contacted?: boolean; register?: boolean; slept?: boolean };

export function ExtensionSend({
  items,
  onUpdate,
  followUpDays = 5,
  pause = pauseMs,
  wake: givenWake,
}: {
  items: OutreachCandidate[];
  onUpdate: (item: OutreachCandidate) => void;
  /** The role's follow-up days (Brief, default 5): set on LinkedIn's own follow-up. */
  followUpDays?: number;
  /** ms to wait between two InMails that went out; tests pass 0. */
  pause?: () => number;
  /** Tests pass their own. */
  wake?: WakeDetector;
}) {
  const wake = useWakeDetector(givenWake);
  // The detector lives as long as the component, so the unmount cleanup can hold it from the start.
  const wakeRef = useRef(wake);
  const [version, setVersion] = useState<string | null | undefined>(undefined);
  const [confirmed, setConfirmed] = useState(false);
  const [running, setRunning] = useState<'send' | 'fill' | null>(null);
  const [rows, setRows] = useState<Record<string, RowState>>({});
  const [withFollowUp, setWithFollowUp] = useState(true);
  // The pause between two sends: when the next one starts, a clock for the countdown, and Stop.
  const [nextAt, setNextAt] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const stop = useRef(false);
  const [stopping, setStopping] = useState(false);
  const endPause = useRef<(() => void) | null>(null);

  // Leaving the page stops the run: nothing goes out without the Send card on screen (CodeRabbit, PR #765).
  useEffect(
    () => () => {
      stop.current = true;
      endPause.current?.();
      wakeRef.current.cancelWaits();
    },
    []
  );

  useEffect(() => {
    if (nextAt === null) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [nextAt]);

  /**
   * Wait `ms`, or less when Stop is pressed. The end is a clock time: a timer does not run while the
   * Mac sleeps, so on wake the pause ends at once if its time is up (Nov 2 2026).
   */
  function wait(ms: number) {
    if (ms <= 0) return Promise.resolve();
    const until = Date.now() + ms;
    setNow(Date.now());
    setNextAt(until);
    let offWake = () => {};
    return new Promise<void>((resolve) => {
      const t = setTimeout(resolve, ms);
      const end = () => {
        clearTimeout(t);
        resolve();
      };
      endPause.current = end;
      offWake = wake.onWake(() => {
        setNow(Date.now());
        if (Date.now() >= until) end();
      });
    }).finally(() => {
      offWake();
      endPause.current = null;
      setNextAt(null);
    });
  }

  function stopRun() {
    stop.current = true;
    setStopping(true);
    endPause.current?.();
    // Waiting offline for the network: ends now (CodeRabbit, PR #783).
    wake.cancelWaits();
  }

  useEffect(() => {
    let alive = true;
    void extensionVersion().then((v) => alive && setVersion(v));
    return () => {
      alive = false;
    };
  }, []);

  const setRow = (id: string, s: RowState) => setRows((r) => ({ ...r, [id]: s }));
  const [rowBusy, setRowBusy] = useState<string | null>(null);

  /**
   * Someone LinkedIn says was already contacted: record them as sent (it was us, e.g. from Recruiter
   * directly) or drop them from this role. Either way they leave the queue; nothing goes to LinkedIn.
   */
  async function settleContacted(c: OutreachCandidate, how: 'sent' | 'drop') {
    setRowBusy(c.id);
    try {
      const r = how === 'sent' ? await api.markOutreachSent(c.id, c.draft?.subject || '', c.draft?.body || '') : await api.dropOutreach(c.id);
      onUpdate(r.item);
      setRows((rows) => {
        const next = { ...rows };
        delete next[c.id];
        return next;
      });
      showToast({ status: 'success', description: how === 'sent' ? `${c.name} is now under Contacted.` : `${c.name} was dropped from this role.` });
    } catch (err) {
      showToast({ status: 'error', position: 'bottomCenter', description: err instanceof Error ? err.message : 'Could not save that.' });
    } finally {
      setRowBusy(null);
    }
  }

  async function start(mode: 'send' | 'fill') {
    const all = items.filter((c) => composerUrl(c) && c.recruiterId);
    // A real run sends the first 25; Test fill presses nothing and goes through all.
    const list = mode === 'send' ? all.slice(0, SEND_BATCH) : all;
    if (!list.length) return;
    stop.current = false;
    setStopping(false);
    setRunning(mode);
    // 0.9.0 (Sofia, Nov 2 2026): ONE LinkedIn window beside this one for the whole run — this window
    // is narrowed once now and restored once at the end. Older extensions: a tab per InMail.
    const inWindow = canSendInWindow(version);
    let keepWindow = false;
    if (inWindow) {
      const opened = await openSendWindow();
      if (!opened.ok) {
        showToast({ status: 'error', position: 'bottomCenter', description: explain(opened) });
        setRunning(null);
        return;
      }
    }
    setRows(Object.fromEntries(list.map((c) => [c.id, { kind: 'waiting' } as RowState])));
    let sent = 0;
    let stoppedAt: string | null = null;
    let pauseFirst = false;
    let slept = false;
    for (const row of list) {
      // A person's pace: a pause after every InMail that went out, never after the last one.
      if (pauseFirst && !stop.current) {
        const pausedAt = Date.now();
        await wait(pause());
        // Woke up or came back online during the pause: on with the next one once the network is there.
        wake.check();
        if (!stop.current && wake.disturbedSince(pausedAt)) await wake.waitUntilOnline();
      }
      pauseFirst = false;
      if (stop.current) break;
      let c = row;
      setRow(c.id, { kind: 'working' });
      // A colleague's draft signed for another sender is put together for the sender first, Test
      // fill included — nothing signed by someone else reaches the composer (Sofia, Oct 28 2026).
      if (c.sentAsOther) {
        const r = await signForMe(c.id);
        if ('why' in r) {
          // This one is held, the rest go on.
          setRow(c.id, { kind: 'failed', text: `Signed by ${c.draft?.sentAs?.senderName || 'a colleague'}; can’t be put together as you: ${r.why}. Open it and press “Update”.` });
          continue;
        }
        const before = c;
        c = r.item;
        onUpdate(c);
        // Only a hand-edited draft comes back unapproved: its edits are gone, so it is held (CodeRabbit,
        // PR #753; Sofia, Oct 28 2026). An unedited one changed only code-filled lines and goes out.
        if (c.status !== 'ready') {
          setRow(c.id, { kind: 'failed', text: `${editsReplacedBy(before)}’s edits were replaced when it was put together as you — check the text and approve again.` });
          continue;
        }
      }
      // Before LinkedIn is even opened: did anyone at FINN write to them in the last six months?
      // Recruiter's own check only sees this seat's messages; the contact register sees everyone's.
      if (mode === 'send') {
        try {
          await api.precheckSend(c.id);
        } catch (err) {
          const code = (err as { body?: { code?: string } })?.body?.code;
          const contacted = code === 'contacted';
          setRow(c.id, { kind: 'failed', text: err instanceof Error ? err.message : 'Could not check this candidate.', contacted, register: contacted });
          // The send guard: signed for someone else (Oct 28 2026) — this one is held, the rest go on.
          if (contacted || code === 'sent_as') continue;
          stoppedAt = c.id;
          break;
        }
      }
      // Stop pressed while this one was being signed or checked: LinkedIn is not opened (CodeRabbit, PR #765).
      if (stop.current) break;
      const openedAt = Date.now();
      const r: SendResult = await runInExtension({
        // Inside the role's project when known, so LinkedIn sets "Contacted" (Oct 27 2026); an
        // extension older than 0.8.0 only opens the plain composer.
        url: composerUrl(c, { inProject: canSendInProject(version) }) as string,
        name: c.name,
        subject: c.draft?.subject || '',
        body: c.draft?.body || '',
        send: mode === 'send',
        // Never on a weekend: a Saturday or Sunday becomes the Monday after (Sofia, Oct 28 2026).
        followUp: withFollowUp && c.followUpPreview ? { subject: followUpSubject(c.draft?.subject || ''), body: c.followUpPreview, days: weekdayFollowUpDays(Date.now(), followUpDays) } : null,
        // The TA managers who test the flow keep their LinkedIn tab open, to look at it (Oct 19 2026).
        // …and are written to even though LinkedIn says "contacted" (test mode only, Oct 19 2026).
        // In the run's window (0.9.0) their tab closes at the end like everyone's.
        ...(c.testRecipient ? { ...(inWindow ? {} : { closeTab: false }), testRecipient: true } : {}),
        ...(inWindow ? { inWindow: true, screen: screenArea() } : {}),
      });
      // Asleep or offline while this InMail was open: whether it went out is unknown — stop, never retry.
      wake.check();
      if (mode === 'send' && !r.ok && wake.disturbedSince(openedAt)) {
        slept = true;
        if (r.error === 'already_contacted') setRow(c.id, { kind: 'failed', text: explain(r), contacted: true });
        else {
          setRow(c.id, { kind: 'failed', text: SLEPT_TEXT, slept: true });
          keepWindow = true; // 0.9.0: the LinkedIn window stays open — the row says to check it there
        }
        stoppedAt = c.id;
        break;
      }
      if (!r.ok && r.error === 'already_contacted') {
        // Not a fault in the run, just someone we must not write to: skip them, carry on.
        setRow(c.id, { kind: 'failed', text: explain(r), contacted: true });
        continue;
      }
      if (!r.ok) {
        setRow(c.id, { kind: 'failed', text: explain(r) });
        stoppedAt = c.id;
        keepWindow = true; // the LinkedIn window stays open to look at it
        break; // never carry on past a refusal: the next composer may be wrong in the same way
      }
      if (mode === 'fill') {
        setRow(c.id, {
          kind: 'filled',
          sendButton: r.sendButton || 'unknown',
          detail: r.detail || '',
          followUp: Boolean(r.followUpScheduled),
          followUpDays: r.followUpDays ?? null,
          links: r.links,
          paragraphsLost: r.paragraphsLost || null,
          contactedWarning: r.contactedWarning || null,
        });
        continue;
      }
      // It went out. Our own record first — whatever happens next, the InMail is sent.
      const record = () =>
        api.markOutreachSent(
          c.id,
          c.draft?.subject || '',
          c.draft?.body || '',
          r.followUpScheduled ? { inLinkedIn: true, text: c.followUpPreview || '', days: r.followUpDays ?? null } : undefined
        );
      try {
        let rec: Awaited<ReturnType<typeof record>>;
        try {
          rec = await record();
        } catch (err) {
          // Our own record only (nothing goes to LinkedIn): after a sleep, once more when back online.
          wake.check();
          if (!wake.disturbedSince(openedAt)) throw err;
          await wake.waitUntilOnline();
          rec = await record();
        }
        onUpdate(rec.item);
        void logAnalyticsEvent('ta_outreach_marked_sent', { lever: rec.item.lever?.status || 'none', test_mode: rec.testMode, via: 'extension' });
      } catch {
        showToast({
          status: 'error',
          position: 'bottomCenter',
          description: `${c.name}: sent in LinkedIn, but could not be recorded here. Use “Mark as sent” on the card.`,
        });
      }
      sent += 1;
      setRow(c.id, { kind: 'sent', verified: r.verified !== false });
      pauseFirst = true;
      // Sent, but the Mac slept or the network dropped meanwhile: the run stops here all the same.
      wake.check();
      if (mode === 'send' && wake.disturbedSince(openedAt)) {
        slept = true;
        break;
      }
    }
    // All done, Stop or page left: the LinkedIn window closes and this window gets its size back.
    if (inWindow) await closeSendWindow(keepWindow);
    // Rows the run did not reach (Stop) show nothing again.
    setRows((rs) => Object.fromEntries(Object.entries(rs).filter(([, v]) => v.kind !== 'waiting')));
    setRunning(null);
    setConfirmed(false);
    const left = all.length - sent;
    if (slept && left > 0) {
      showToast({ status: 'info', position: 'bottomCenter', description: `The laptop slept during an InMail, so the run stopped: ${sent} sent, ${left} still to go — press Start to continue.` });
    } else if (mode === 'send' && !stoppedAt && left > 0 && (stop.current || all.length > list.length)) {
      showToast({ status: 'info', position: 'bottomCenter', description: `${sent} sent, ${left} still to go — press Start for the next ${Math.min(left, SEND_BATCH)}.` });
    }
    void logAnalyticsEvent('ta_outreach_send_run_finished', { total: list.length, sent, mode, stopped: Boolean(stoppedAt) || stop.current || slept });
  }

  const n = items.length;
  const noId = items.filter((c) => !c.recruiterId);
  const runnable = n - noId.length;
  const batch = Math.min(runnable, SEND_BATCH);

  return (
    <Card className="space-y-3 text-[13px]">
      <h3 className="text-base font-semibold">
        Send · {n} approved InMail{n === 1 ? '' : 's'}
      </h3>

      <ExtensionSetup version={version} />

      {noId.map((c) => (
        <RecruiterLinkFix key={c.id} candidate={c} onSaved={onUpdate} />
      ))}

      {n > 0 && (
        <ol className="space-y-1">
          {items.map((c) => {
            const s = rows[c.id];
            return (
              <li key={c.id} className="flex flex-wrap items-baseline gap-2">
                <span className="font-medium">{c.name}</span>
                <span className="text-[#707070]">— {c.draft?.subject}</span>
                {!c.recruiterId && <Badge tone="grey">no Recruiter id · skipped</Badge>}
                {c.recruiterId && !sendsInProject(c) && <Badge tone="grey">no Recruiter project · LinkedIn won’t set “Contacted”</Badge>}
                {/* An extension before 0.8.0 opens the plain composer even with a project (CodeRabbit, PR #748). */}
                {c.recruiterId && sendsInProject(c) && version && !canSendInProject(version) && (
                  <Badge tone="grey">update the extension to 0.8.0 · LinkedIn won’t set “Contacted”</Badge>
                )}
                {s?.kind === 'working' && <Badge tone="blue">in LinkedIn…</Badge>}
                {s?.kind === 'filled' && (
                  <>
                    <Badge tone={s.sendButton === 'enabled' ? 'green' : 'amber'}>
                      Filled, not sent — Send button {s.sendButton === 'enabled' ? 'ready' : s.sendButton}
                      {s.followUp ? ` · follow-up added${s.followUpDays ? ` (after ${s.followUpDays} days)` : ''}` : ''}
                      {s.links === 'links' ? ' · links ✓' : ''}
                    </Badge>
                    {s.links === 'urls' && (
                      <span role="alert" className="w-full text-[#B96716]">
                        LinkedIn did not take the links as links: they were typed as “words (address)”. “Start” would stop here and send nothing.
                      </span>
                    )}
                    {s.paragraphsLost && (
                      <span role="alert" className="w-full text-[#B96716]">
                        LinkedIn lost the paragraphs ({s.paragraphsLost}). “Start” would stop here and send nothing.
                      </span>
                    )}
                    {s.contactedWarning && (
                      <span role="alert" className="w-full text-[#B96716]">
                        Detected: already contacted — LinkedIn says: “{s.contactedWarning}” Filled for the test only; “Start” would stop here and send nothing.
                      </span>
                    )}
                    {s.detail && <span className="w-full text-xs text-[#707070]">{s.detail}</span>}
                  </>
                )}
                {s?.kind === 'sent' && <Badge tone="green">{s.verified ? 'Sent ✓' : 'Sent (LinkedIn gave no confirmation — check the tab)'}</Badge>}
                {s?.kind === 'failed' && (
                  <span role="alert" className="text-[#E71D3F]">
                    Stopped: {s.text}
                  </span>
                )}
                {s?.kind === 'failed' && s.slept && (
                  <span className="flex w-full flex-wrap gap-2 pl-0.5">
                    <button type="button" className={btnO} disabled={rowBusy === c.id} onClick={() => void settleContacted(c, 'sent')}>
                      Mark as sent
                    </button>
                  </span>
                )}
                {s?.kind === 'failed' && s.contacted && (
                  <span className="flex w-full flex-wrap gap-2 pl-0.5">
                    {!s.register && (
                      <button type="button" className={btnO} disabled={rowBusy === c.id} onClick={() => void settleContacted(c, 'sent')}>
                        Mark as sent
                      </button>
                    )}
                    <button type="button" className={btnO} disabled={rowBusy === c.id} onClick={() => void settleContacted(c, 'drop')}>
                      Drop
                    </button>
                  </span>
                )}
              </li>
            );
          })}
        </ol>
      )}

      <label className="flex items-start gap-2">
        <input type="checkbox" checked={withFollowUp} disabled={running !== null} onChange={(e) => setWithFollowUp(e.target.checked)} className="mt-0.5" />
        <span>
          Add LinkedIn’s own follow-up — LinkedIn sends it after {followUpDays} day{followUpDays === 1 ? '' : 's'} only if there is no reply, and drops it when they
          answer. A weekend moves it to Monday. The days are set per role in step 1.
        </span>
      </label>
      <label className="flex items-start gap-2">
        <input type="checkbox" checked={confirmed} disabled={!version || running !== null} onChange={(e) => setConfirmed(e.target.checked)} className="mt-0.5" />
        <span>
          I checked the {runnable > SEND_BATCH ? `first ${batch}` : batch} InMail{batch === 1 ? '' : 's'} — they go out for real.
        </span>
      </label>
      <div className="flex flex-wrap gap-2">
        <button type="button" className={btnP} disabled={!version || !runnable || !confirmed || running !== null} onClick={() => void start('send')}>
          {running === 'send' ? 'Sending…' : 'Start'}
        </button>
        <button type="button" className={btnO} disabled={!version || !runnable || running !== null} onClick={() => void start('fill')} title="Opens each composer and types the InMail, presses nothing.">
          {running === 'fill' ? 'Filling…' : 'Test fill (no send)'}
        </button>
        {running === 'send' && (
          <button type="button" className={btnO} disabled={stopping} onClick={stopRun} title="Stops after the InMail that is open in LinkedIn now.">
            {stopping ? 'Stopping…' : 'Stop'}
          </button>
        )}
        {nextAt !== null && <span className="self-center text-[#707070]">Next InMail in {clock(nextAt - now)}</span>}
      </div>
      <p className="m-0 text-xs text-[#707070]">
        Up to {SEND_BATCH} per Start, one every 45–120 seconds, at a person’s pace. Keep this page open; you can work in another window —{' '}
        {canSendInWindow(version) ? 'LinkedIn runs in its own window beside this one.' : 'each InMail opens LinkedIn here for a few seconds.'}
      </p>
    </Card>
  );
}
